import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Form, Badge, Alert, Row, Col } from "react-bootstrap";
import FormField from "../components/FormField";
import LoaderButton from "../components/LoaderButton";
import { useDispatch, useSelector } from "react-redux";
import {
  adminGetAllLabs,
  adminCreateLab,
  adminUpdateLab,
  adminDeleteLab,
  adminReactivateLab,
} from "../actions/userActions";

const ManageLabs = () => {
  const dispatch = useDispatch();
  const { labs, loading } = useSelector((state) => state.adminLabs);
  const { loading: mutationLoading, success, error: mutationError } = useSelector((state) => state.adminLabMutation);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedLab, setSelectedLab] = useState(null);
  const [alertMsg, setAlertMsg] = useState(null);

  // Create form
  const [createForm, setCreateForm] = useState({
    com_name: "", address_line1: "", address_line2: "", address_line3: "",
    contact_number: "", landline: "", officeEmail: "", logo: ""
  });

  // Edit form
  const [editForm, setEditForm] = useState({
    id: "", com_name: "", address_line1: "", address_line2: "", address_line3: "",
    contact_number: "", landline: "", officeEmail: "", logo: ""
  });

  useEffect(() => {
    dispatch(adminGetAllLabs());
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      setAlertMsg({ type: "success", text: "Operation completed successfully" });
      setShowCreateModal(false);
      setShowEditModal(false);
      setTimeout(() => setAlertMsg(null), 3000);
    }
    if (mutationError) {
      setAlertMsg({ type: "danger", text: mutationError });
      setTimeout(() => setAlertMsg(null), 5000);
    }
  }, [success, mutationError]);

  const handleCreateSubmit = (e) => {
    e.preventDefault();
    dispatch(adminCreateLab(createForm));
    setCreateForm({
      com_name: "", address_line1: "", address_line2: "", address_line3: "",
      contact_number: "", landline: "", officeEmail: "", logo: ""
    });
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    dispatch(adminUpdateLab(editForm));
  };

  const openEditModal = (lab) => {
    setSelectedLab(lab);
    setEditForm({
      id: lab.id,
      com_name: lab.com_name || "",
      address_line1: lab.address_line1 || "",
      address_line2: lab.address_line2 || "",
      address_line3: lab.address_line3 || "",
      contact_number: lab.contact_number || "",
      landline: lab.landline || "",
      officeEmail: lab.officeEmail || "",
      logo: lab.logo || "",
    });
    setShowEditModal(true);
  };

  const handleLogoChange = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("Please select a valid image file (PNG/JPG/JPEG).");
        return;
      }
      if (file.size > 200 * 1024) {
        alert("Please upload an image smaller than 200KB to optimize performance.");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        if (isEdit) {
          setEditForm((prev) => ({ ...prev, logo: reader.result }));
        } else {
          setCreateForm((prev) => ({ ...prev, logo: reader.result }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const getStatusBadge = (isActive) => {
    return isActive ? <Badge variant="success">Active</Badge> : <Badge variant="danger">Deleted</Badge>;
  };

  return (
    <div className="manage-labs-screen pb-5 mt-3">
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <h3 className="font-weight-bold grey-900 mb-1" style={{ letterSpacing: '-0.8px', fontSize: 'clamp(1rem, 4vw, 1.4rem)' }}>Manage Laboratories</h3>
          <p className="text-muted mb-0">Configure lab locations, addresses, and contact profiles</p>
        </div>
        <Button
          variant="primary"
          onClick={() => setShowCreateModal(true)}
          style={{ fontWeight: 600, height: '40px', padding: '0 24px', borderRadius: '8px', minWidth: '180px' }}
        >
          <i className="fa fa-plus-circle mr-2"></i> Create Lab
        </Button>
      </div>

      {alertMsg && (
        <Alert variant={alertMsg.type} className="custom-card-boxshadow mb-4" dismissible onClose={() => setAlertMsg(null)}>
          {alertMsg.text}
        </Alert>
      )}

      <div className="billing-form-card custom-card-boxshadow p-0 overflow-hidden">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="mt-3 text-muted">Fetching laboratory catalog...</p>
          </div>
        ) : (
          <div className="premium-table-container">
            <div className="premium-table-header d-flex justify-content-between align-items-center">
              <span className="small font-weight-bold text-muted text-uppercase letter-spacing-1">Labs</span>
            </div>
            <div className="table-responsive">
              <Table hover className="mb-0 border-0" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                <thead>
                  <tr>
                    <th className="text-center py-3 border-0" width="60">#</th>
                    <th className="py-3 border-0">Laboratory Name</th>
                    <th className="py-3 border-0">Primary Address</th>
                    <th className="py-3 border-0">Contact Number</th>
                    <th className="py-3 border-0">Office Email</th>
                    <th className="text-center py-3 border-0">Users</th>
                    <th className="py-3 border-0">Status</th>
                    <th className="text-center py-3 border-0" width="180">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {labs && labs.length > 0 ? labs.map((lab, index) => (
                    <tr key={lab.id}>
                      <td className="text-center align-middle border-bottom font-weight-bold">{index + 1}</td>
                      <td className="align-middle border-bottom font-weight-bold">
                        <div className="d-flex align-items-center">
                          {lab.logo ? (
                            <img
                              src={lab.logo.startsWith("data:image") ? lab.logo : `data:image/jpeg;base64,${lab.logo}`}
                              alt="Logo"
                              style={{ width: '36px', height: '36px', objectFit: 'contain', borderRadius: '6px', marginRight: '12px', border: '1px solid #e9ecef', padding: '2px', backgroundColor: '#fff' }}
                            />
                          ) : (
                            <div
                              className="d-flex align-items-center justify-content-center bg-light text-muted font-weight-bold"
                              style={{ width: '36px', height: '36px', borderRadius: '6px', marginRight: '12px', fontSize: '11px', border: '1px solid #e9ecef', textTransform: 'uppercase' }}
                            >
                              {lab.com_name ? lab.com_name.substring(0, 2) : 'LA'}
                            </div>
                          )}
                          <span>{lab.com_name}</span>
                        </div>
                      </td>
                      <td className="align-middle border-bottom text-muted small">
                        {[lab.address_line1, lab.address_line2].filter(Boolean).join(', ')}
                      </td>
                      <td className="align-middle border-bottom font-weight-medium">{lab.contact_number}</td>
                      <td className="align-middle border-bottom text-primary small font-weight-medium">{lab.officeEmail}</td>
                      <td className="text-center align-middle border-bottom">
                        <Badge variant="primary" style={{ minWidth: '35px' }}>{lab.user_count}</Badge>
                      </td>
                      <td className="align-middle border-bottom">{getStatusBadge(lab.is_active)}</td>
                      <td className="text-center align-middle border-bottom">
                        <div className="d-flex justify-content-center align-items-center" style={{ gap: '15px' }}>
                          <Button
                            variant="link"
                            title="Edit Profile"
                            className="p-0 text-warning"
                            onClick={() => openEditModal(lab)}
                          >
                            <i className="fa fa-pencil-square fa-lg"></i>
                          </Button>
                          {lab.is_active ? (
                            <Button
                              variant="link"
                              title="Deactivate Lab"
                              className="p-0 text-danger"
                              onClick={() => {
                                if (window.confirm(`Deactivate the lab "${lab.com_name}"? This will deactivate the lab and all its users.`)) {
                                  dispatch(adminDeleteLab(lab.id));
                                }
                              }}
                            >
                              <i className="fa fa-trash fa-lg"></i>
                            </Button>
                          ) : (
                            <Button
                              variant="link"
                              title="Reactivate"
                              className="p-0 text-success"
                              onClick={() => dispatch(adminReactivateLab(lab.id))}
                            >
                              <i className="fa fa-refresh fa-lg"></i>
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="8" className="text-center p-5 text-muted">No laboratories found</td></tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </div>

      <Alert variant="info" className="mt-4 custom-card-boxshadow bg-white text-info border-info-light">
        <div className="d-flex align-items-center">
          <i className="fa fa-info-circle fa-lg mr-3"></i>
          <div>
            <p className="mb-0 font-weight-bold">Soft Delete Policy</p>
            <small className="opacity-8">Deactivating a lab performs a "soft delete." The lab and its users will lose creative access, but all existing financial data remains intact for auditing.</small>
          </div>
        </div>
      </Alert>

      <Modal centered show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" backdrop="static">
        <Modal.Header className="bg-light pb-3" closeButton>
          <Modal.Title className="font-weight-bold grey-900">Establish New Laboratory</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateSubmit}>
          <Modal.Body className="p-4">
            <Row>
              <Col xs={12} className="text-center mb-4">
                <div className="d-inline-block position-relative">
                  {createForm.logo ? (
                    <img
                      src={createForm.logo.startsWith("data:image") ? createForm.logo : `data:image/jpeg;base64,${createForm.logo}`}
                      alt="Preview"
                      style={{ width: '100px', height: '100px', objectFit: 'contain', borderRadius: '10px', border: '2px solid #e9ecef', padding: '4px', backgroundColor: '#fff' }}
                    />
                  ) : (
                    <div
                      style={{ width: '100px', height: '100px', borderRadius: '10px', background: '#f8f9fa', border: '2px dashed #ccc', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '11px' }}
                    >
                      <i className="fa fa-picture-o fa-2x mb-1" style={{ color: '#ccc' }}></i>
                      <span>No Logo</span>
                    </div>
                  )}
                  {createForm.logo && (
                    <button
                      type="button"
                      onClick={() => setCreateForm(prev => ({ ...prev, logo: '' }))}
                      className="btn btn-sm btn-danger position-absolute"
                      style={{ top: '-10px', right: '-10px', borderRadius: '50%', width: '24px', height: '24px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', lineHeight: 1 }}
                    >
                      &times;
                    </button>
                  )}
                </div>
                <div className="mt-2">
                  <label className="btn btn-sm btn-outline-primary mb-0" style={{ cursor: 'pointer', fontWeight: 600 }}>
                    <i className="fa fa-upload mr-1"></i> Upload Logo
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => handleLogoChange(e, false)}
                    />
                  </label>
                  <p className="text-muted small mb-0 mt-1">Max 200KB. PNG or JPG.</p>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormField label="Lab Name" mandatory>
                  <Form.Control
                    type="text"
                    className="bg-light border-0"
                    required
                    placeholder="Enter full lab name"
                    value={createForm.com_name}
                    onChange={(e) => setCreateForm({ ...createForm, com_name: e.target.value })}
                  />
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="Official Email Address">
                  <Form.Control
                    type="email"
                    className="bg-light border-0"
                    placeholder="office@lab.com"
                    value={createForm.officeEmail}
                    onChange={(e) => setCreateForm({ ...createForm, officeEmail: e.target.value })}
                  />
                </FormField>
              </Col>
              <Col md={12}>
                <FormField label="Street Address">
                  <Form.Control
                    type="text"
                    className="bg-light border-0"
                    placeholder="Line 1"
                    value={createForm.address_line1}
                    onChange={(e) => setCreateForm({ ...createForm, address_line1: e.target.value })}
                  />
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="City / Area">
                  <Form.Control
                    type="text"
                    className="bg-light border-0"
                    placeholder="Line 2"
                    value={createForm.address_line2}
                    onChange={(e) => setCreateForm({ ...createForm, address_line2: e.target.value })}
                  />
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="State / PIN">
                  <Form.Control
                    type="text"
                    className="bg-light border-0"
                    placeholder="Line 3"
                    value={createForm.address_line3}
                    onChange={(e) => setCreateForm({ ...createForm, address_line3: e.target.value })}
                  />
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="Primary Contact">
                  <Form.Control
                    type="text"
                    className="bg-light border-0"
                    placeholder="+91"
                    value={createForm.contact_number}
                    onChange={(e) => setCreateForm({ ...createForm, contact_number: e.target.value })}
                  />
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="Landline Support">
                  <Form.Control
                    type="text"
                    className="bg-light border-0"
                    placeholder="STD-Phone"
                    value={createForm.landline}
                    onChange={(e) => setCreateForm({ ...createForm, landline: e.target.value })}
                  />
                </FormField>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="bg-light border-0 p-3">
            <Button variant="outline-secondary" style={{ fontWeight: 600, height: '40px', padding: '0 24px', borderRadius: '8px' }} onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <LoaderButton isLoading={mutationLoading} variant="primary" type="submit">
              Save Laboratory Profile
            </LoaderButton>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal centered show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" backdrop="static">
        <Modal.Header className="bg-light pb-3" closeButton>
          <Modal.Title className="font-weight-bold grey-900">Update Lab Profile: {selectedLab?.com_name}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditSubmit}>
          <Modal.Body className="p-4">
            <Row>
              <Col xs={12} className="text-center mb-4">
                <div className="d-inline-block position-relative">
                  {editForm.logo ? (
                    <img
                      src={editForm.logo.startsWith("data:image") ? editForm.logo : `data:image/jpeg;base64,${editForm.logo}`}
                      alt="Preview"
                      style={{ width: '100px', height: '100px', objectFit: 'contain', borderRadius: '10px', border: '2px solid #e9ecef', padding: '4px', backgroundColor: '#fff' }}
                    />
                  ) : (
                    <div
                      style={{ width: '100px', height: '100px', borderRadius: '10px', background: '#f8f9fa', border: '2px dashed #ccc', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '11px' }}
                    >
                      <i className="fa fa-picture-o fa-2x mb-1" style={{ color: '#ccc' }}></i>
                      <span>No Logo</span>
                    </div>
                  )}
                  {editForm.logo && (
                    <button
                      type="button"
                      onClick={() => setEditForm(prev => ({ ...prev, logo: '' }))}
                      className="btn btn-sm btn-danger position-absolute"
                      style={{ top: '-10px', right: '-10px', borderRadius: '50%', width: '24px', height: '24px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', lineHeight: 1 }}
                    >
                      &times;
                    </button>
                  )}
                </div>
                <div className="mt-2">
                  <label className="btn btn-sm btn-outline-primary mb-0" style={{ cursor: 'pointer', fontWeight: 600 }}>
                    <i className="fa fa-upload mr-1"></i> Upload Logo
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => handleLogoChange(e, true)}
                    />
                  </label>
                  <p className="text-muted small mb-0 mt-1">Max 200KB. PNG or JPG.</p>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormField label="Laboratory Name">
                  <Form.Control
                    type="text"
                    className="bg-light border-0"
                    placeholder="Name"
                    value={editForm.com_name}
                    onChange={(e) => setEditForm({ ...editForm, com_name: e.target.value })}
                  />
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="Official Contact Email">
                  <Form.Control
                    type="email"
                    className="bg-light border-0"
                    placeholder="Email"
                    value={editForm.officeEmail}
                    onChange={(e) => setEditForm({ ...editForm, officeEmail: e.target.value })}
                  />
                </FormField>
              </Col>
              <Col md={12}>
                <FormField label="Street Address Line 1">
                  <Form.Control
                    type="text"
                    className="bg-light border-0"
                    placeholder="Line 1"
                    value={editForm.address_line1}
                    onChange={(e) => setEditForm({ ...editForm, address_line1: e.target.value })}
                  />
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="Line 2 (City / Landmark)">
                  <Form.Control
                    type="text"
                    className="bg-light border-0"
                    placeholder="Line 2"
                    value={editForm.address_line2}
                    onChange={(e) => setEditForm({ ...editForm, address_line2: e.target.value })}
                  />
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="Line 3 (State / Region)">
                  <Form.Control
                    type="text"
                    className="bg-light border-0"
                    placeholder="Line 3"
                    value={editForm.address_line3}
                    onChange={(e) => setEditForm({ ...editForm, address_line3: e.target.value })}
                  />
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="Mobile Number">
                  <Form.Control
                    type="text"
                    className="bg-light border-0"
                    placeholder="Contact"
                    value={editForm.contact_number}
                    onChange={(e) => setEditForm({ ...editForm, contact_number: e.target.value })}
                  />
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="Landline support">
                  <Form.Control
                    type="text"
                    className="bg-light border-0"
                    placeholder="Landline"
                    value={editForm.landline}
                    onChange={(e) => setEditForm({ ...editForm, landline: e.target.value })}
                  />
                </FormField>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="bg-light border-0 p-3">
            <Button variant="outline-secondary" style={{ fontWeight: 600, height: '40px', padding: '0 24px', borderRadius: '8px' }} onClick={() => setShowEditModal(false)}>Cancel</Button>
            <LoaderButton isLoading={mutationLoading} variant="primary" type="submit">
              Commit Changes
            </LoaderButton>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default ManageLabs;
